import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/utils/api';
import { useApp } from '@/components/hooks/useApp.jsx';
import { toastApiError } from '@/lib/query-client';

/**
 * Reads the per-user UI preferences bucket (users.preferences Json column).
 * The backend exposes a shallow-merge contract — calling update({pinned_modules})
 * leaves any other key (density, locale_override, ...) intact.
 *
 * Contract is documented in app/docs/decisions/0001-pinned-modules-storage.md.
 * The backend route lives at /api/users/me/preferences (Zod .strict() — adding
 * a new key requires updating PreferencesShape there).
 *
 * Cache key is per user id (NOT per company): preferences are user-scoped.
 *
 * Usage:
 *   const { preferences, update, isLoading } = useUserPreferences();
 *   update({ pinned_modules: ['Facturas', 'Bancos'] });
 *
 * The mutation merges optimistically: callers see the new value immediately
 * via setQueryData, then the server confirms (or revalidates on error).
 */
export function useUserPreferences() {
  const { user } = useApp();
  const queryClient = useQueryClient();
  const queryKey = ['user-preferences', user?.id];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await apiFetch('/api/users/me/preferences');
      return res?.preferences ?? {};
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (patch) =>
      apiFetch('/api/users/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onMutate: async (patch) => {
      // Optimistic shallow merge — the UI shouldn't lag a network round-trip
      // when the user clicks a star or toggles density. Server confirms next.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey) ?? {};
      queryClient.setQueryData(queryKey, { ...previous, ...patch });
      return { previous };
    },
    onError: (error, _patch, context) => {
      // Roll back on error and surface the toast.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toastApiError(error, 'No se pudo guardar la preferencia');
    },
    onSuccess: (data) => {
      // Server response wins (in case of merge conflicts).
      queryClient.setQueryData(queryKey, data?.preferences ?? {});
    },
  });

  return {
    preferences: query.data ?? {},
    isLoading: query.isLoading,
    isError: query.isError,
    update: (patch) => mutation.mutate(patch),
    isUpdating: mutation.isPending,
  };
}
